/**
 * The address of a document is a KrewUp address, forever: `/drive/document/:id` for a Drive file, `/attachments/:id` for the file attached to a record.
 * Nothing here knows a Storage URL, a bucket or a token. The bytes come from the backend (`GET api/drive/files/:id/content`, `GET api/attachments/:id/content`),
 * which checks the person's access again on every request and streams the file; the browser shows them from a local `blob:` URL, so the address bar
 * (and anything the person copies) stays on the KrewUp domain.
 */
import { useEffect, useState } from 'react';
import { getApiUrl } from './api-config';

export type DocumentKind = 'drive' | 'attachment';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The permanent in-app path of a document (what the visible address and every link use). */
export const documentPath = (kind: DocumentKind, id: string): string => (kind === 'drive' ? `/drive/document/${id}` : `/attachments/${id}`);

/** The same address with the KrewUp domain in front, for places that need a full URL (copying, emails). */
export const documentUrl = (kind: DocumentKind, id: string): string => `${window.location.origin}${documentPath(kind, id)}`;

/** The backend route that streams a document's bytes. */
export const documentContentApiPath = (kind: DocumentKind, id: string, opts: { download?: boolean } = {}): string =>
  `${kind === 'drive' ? `api/drive/files/${id}/content` : `api/attachments/${id}/content`}${opts.download ? '?download=1' : ''}`;

/** `/drive/document/:id` or `/attachments/:id` (absolute or relative) -> which document it is; null for anything else. */
export function parseDocumentLocation(value: string | null | undefined): { kind: DocumentKind; id: string } | null {
  if (!value) return null;
  let pathname = value;
  try {
    pathname = new URL(value, 'https://app.invalid').pathname;
  } catch {
    return null;
  }
  const match = /^\/(?:(drive)\/document|(attachments))\/([^/]+)\/?$/.exec(pathname);
  if (!match || !UUID.test(match[3])) return null;
  return { kind: match[1] ? 'drive' : 'attachment', id: match[3] };
}

export class ProtectedFileError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ProtectedFileError';
  }
  /** The person is signed in but this document is not theirs to open (or no longer exists). */
  get isAccessDenied() {
    return this.status === 403 || this.status === 404;
  }
  get isSignedOut() {
    return this.status === 401;
  }
}

export interface ProtectedFile {
  blob: Blob;
  fileName: string;
  contentType: string;
}

/** The original name from `Content-Disposition` (RFC 5987 `filename*` first, then the quoted ASCII fallback). */
export function fileNameFromDisposition(header: string | null | undefined, fallback = 'file'): string {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* use the fallback form */
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(header);
  return plain?.[1] || fallback;
}

/** Fetches a document through the backend with the person's session (the app's authenticated fetch adds it). */
export async function fetchProtectedFile(kind: DocumentKind, id: string, opts: { signal?: AbortSignal } = {}): Promise<ProtectedFile> {
  if (!UUID.test(id)) throw new ProtectedFileError(404, 'Document not found');
  let response: Response;
  try {
    response = await fetch(getApiUrl(documentContentApiPath(kind, id)), { signal: opts.signal });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new ProtectedFileError(0, 'The file could not be loaded. Check your connection and try again.');
  }
  if (!response.ok) throw new ProtectedFileError(response.status, response.status === 403 || response.status === 404 ? 'Access denied' : 'The file could not be loaded right now.');
  const blob = await response.blob();
  return { blob, fileName: fileNameFromDisposition(response.headers.get('content-disposition')), contentType: response.headers.get('content-type') || blob.type || 'application/octet-stream' };
}

/** Saves a blob under its original name without leaving the page. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Download button / menu: the file arrives through KrewUp and is saved with its original name. */
export async function downloadProtectedFile(kind: DocumentKind, id: string, fallbackName?: string): Promise<void> {
  const file = await fetchProtectedFile(kind, id);
  saveBlob(file.blob, fallbackName || file.fileName);
}

/** Opens the permanent address in a new tab (the viewer there checks the session and the access). */
export function openDocumentInNewTab(kind: DocumentKind, id: string): void {
  window.open(documentPath(kind, id), '_blank', 'noopener,noreferrer');
}

// -- thumbnails: a grid of pictures must not start hundreds of downloads at once --------------------------------------------------------------
const MAX_CONCURRENT = 4;
let running = 0;
const waiting: Array<() => void> = [];
function acquire(): Promise<void> {
  if (running < MAX_CONCURRENT) {
    running += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => waiting.push(() => { running += 1; resolve(); }));
}
function release() {
  running -= 1;
  const next = waiting.shift();
  if (next) next();
}

export type ProtectedUrlState = { status: 'loading' | 'error'; url: null } | { status: 'ready'; url: string };

/**
 * A `blob:` URL for a document's bytes, for `<img src>` / `<iframe src>`. Loaded once per document, a few at a time, and released when the
 * component goes away. `error` means it could not be shown (no access, deleted, offline): callers fall back to an icon.
 */
export function useProtectedObjectUrl(kind: DocumentKind, id: string | null | undefined, enabled = true): ProtectedUrlState {
  const [state, setState] = useState<ProtectedUrlState>({ status: 'loading', url: null });
  useEffect(() => {
    if (!enabled || !id) {
      setState({ status: 'error', url: null });
      return;
    }
    setState({ status: 'loading', url: null });
    let cancelled = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();
    (async () => {
      await acquire();
      try {
        if (cancelled) return;
        const file = await fetchProtectedFile(kind, id, { signal: controller.signal });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(file.blob);
        setState({ status: 'ready', url: objectUrl });
      } catch {
        if (!cancelled) setState({ status: 'error', url: null });
      } finally {
        release();
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [kind, id, enabled]);
  return state;
}
